import { useState, useMemo, type ReactNode } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Ticket as TicketIcon, Search, XCircle, Trash2 } from 'lucide-react'
import { api } from '../lib/api'
import { formatCurrency } from '../lib/utils'
import { useAuth } from '../context/AuthContext'
import CsvControls from '../components/CsvControls'

// Admin cancel-reason dropdown — mirrors TICKET_CANCEL_REASON_CODES in
// backend/worker/src/routes/admin/events.ts.
const CANCEL_REASONS: { code: string; label: string }[] = [
  { code: 'customer_request', label: 'Customer request' },
  { code: 'event_cancelled',  label: 'Event cancelled' },
  { code: 'duplicate',        label: 'Duplicate booking' },
  { code: 'no_show',          label: 'No-show' },
  { code: 'test_entry',       label: 'Test entry' },
  { code: 'other',            label: 'Other (specify)' },
]

// All-bookings hub. Cross-event view with search + filters + CSV
// export. Lives alongside Events (per-event drilldown still happens
// in Events → row → BookingsModal) but solves the "who's coming this
// weekend?" / "show me all cancelled card-paid bookings" queries
// without forcing the operator to open each event individually.

interface Booking {
  id: string
  event_id: string
  user_id: string
  quantity: number
  total_paid_fils: number
  payment_method: string
  status: 'confirmed' | 'cancelled' | 'used'
  redemption_code: string
  created_at: string
  cancelled_at: string | null
  attendance_date: string | null
  attendance_time: string | null
  card_brand: string | null
  card_last4: string | null
  refund_transaction_id: string | null
  refunded_at: string | null
  user_name: string
  user_phone: string
  event_name_en: string
  event_name_ar: string
  event_type: string
  event_starts_at: string
  tier_name: string | null
}

interface EventOption {
  id: string
  name_en: string
}

const STATUSES = ['', 'confirmed', 'cancelled', 'used'] as const
const PAYMENT_METHODS = ['', 'cod', 'card', 'card_on_delivery', 'cliq'] as const
const EVENT_TYPES = ['', 'whisky_tasting', 'wine_tasting', 'mixology', 'other'] as const
const SORTS = [
  { v: 'event_date_desc', label: 'Visit date · newest first' },
  { v: 'event_date_asc',  label: 'Visit date · oldest first' },
  { v: 'booked_desc',     label: 'Booked at · newest first' },
  { v: 'total_desc',      label: 'Total paid · highest first' },
] as const

export default function Bookings() {
  const [q, setQ] = useState('')
  const [status, setStatus] = useState<typeof STATUSES[number]>('')
  const [paymentMethod, setPaymentMethod] = useState<typeof PAYMENT_METHODS[number]>('')
  const [eventType, setEventType] = useState<typeof EVENT_TYPES[number]>('')
  const [eventId, setEventId] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [sort, setSort] = useState<typeof SORTS[number]['v']>('event_date_desc')

  // Build the querystring shared between the data query and the CSV
  // export — keeps the export honouring the same filter set the
  // operator is looking at. status='all' is encoded as no param.
  const queryStr = useMemo(() => {
    const p = new URLSearchParams()
    if (q.trim()) p.set('q', q.trim())
    if (status) p.set('status', status)
    if (paymentMethod) p.set('payment_method', paymentMethod)
    if (eventType) p.set('event_type', eventType)
    if (eventId) p.set('event_id', eventId)
    if (from) p.set('from', from)
    if (to) p.set('to', to)
    if (sort) p.set('sort', sort)
    return p.toString()
  }, [q, status, paymentMethod, eventType, eventId, from, to, sort])

  const { data, isLoading } = useQuery<{ bookings: Booking[]; total: number }>({
    queryKey: ['admin-all-bookings', queryStr],
    queryFn: () => api.get(`/events/bookings/all?${queryStr}`).then(r => r.data),
  })

  // Lightweight event list for the event filter dropdown. Reuses the
  // admin events GET so we get the same status=all behaviour the
  // Events page already pages through.
  const { data: eventsData } = useQuery<{ events: EventOption[] }>({
    queryKey: ['admin-events-options'],
    queryFn: () => api.get('/events?status=all').then(r => r.data),
  })

  const { user } = useAuth()
  const canCancel = user?.role === 'super_admin' || user?.role === 'operations'
  const canDelete = user?.role === 'super_admin'
  const qc = useQueryClient()
  const [cancelTarget, setCancelTarget] = useState<Booking | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Booking | null>(null)
  const refresh = () => qc.invalidateQueries({ queryKey: ['admin-all-bookings'] })

  const bookings = data?.bookings ?? []
  const totals = bookings.reduce((acc, b) => {
    if (b.status === 'cancelled') {
      acc.cancelled += b.quantity
      if (b.refund_transaction_id) acc.refunded += b.total_paid_fils
    } else {
      acc.attendees += b.quantity
      acc.revenue   += b.total_paid_fils
      if (b.status === 'used') acc.checkedIn += b.quantity
    }
    return acc
  }, { attendees: 0, revenue: 0, checkedIn: 0, cancelled: 0, refunded: 0 })

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <TicketIcon size={24} className="text-[#C41E3A]" />
          <div>
            <h1 className="text-2xl font-bold text-white">All Bookings</h1>
            <p className="text-xs text-gray-500">{data?.total ?? 0} bookings · cross-event view with filters + CSV export</p>
          </div>
        </div>
        <CsvControls
          basePath="/events/bookings/all"
          exportFilename={`bookings-${new Date().toISOString().slice(0, 10)}.csv`}
          exportOnly
          exportQuery={queryStr ? `?${queryStr}` : ''}
        />
      </div>

      {/* Filters — keep them in one strip so the operator can adjust
          everything without losing the table out of view. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Search name or phone…"
            value={q}
            onChange={e => setQ(e.target.value)}
            className="w-full pl-8 pr-3 py-2 rounded-lg bg-white/5 border border-[#2e2e2e] text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-[#C41E3A]/40"
          />
        </div>
        <select
          value={eventId}
          onChange={e => setEventId(e.target.value)}
          className={selectCls}
        >
          <option value="">All events</option>
          {(eventsData?.events ?? []).map(e => (
            <option key={e.id} value={e.id}>{e.name_en}</option>
          ))}
        </select>
        <select value={status} onChange={e => setStatus(e.target.value as typeof STATUSES[number])} className={selectCls}>
          <option value="">All statuses</option>
          <option value="confirmed">Confirmed</option>
          <option value="cancelled">Cancelled</option>
          <option value="used">Checked in</option>
        </select>
        <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value as typeof PAYMENT_METHODS[number])} className={selectCls}>
          <option value="">All payment methods</option>
          <option value="card">Card (online)</option>
          <option value="card_on_delivery">Card on arrival</option>
          <option value="cod">Cash</option>
          <option value="cliq">CliQ</option>
        </select>
        <select value={eventType} onChange={e => setEventType(e.target.value as typeof EVENT_TYPES[number])} className={selectCls}>
          <option value="">All experience types</option>
          <option value="whisky_tasting">Whisky tasting</option>
          <option value="wine_tasting">Wine tasting</option>
          <option value="mixology">Mixology</option>
          <option value="other">Other</option>
        </select>
        <input
          type="date"
          value={from}
          onChange={e => setFrom(e.target.value)}
          placeholder="From"
          aria-label="From date"
          className={selectCls}
        />
        <input
          type="date"
          value={to}
          onChange={e => setTo(e.target.value)}
          placeholder="To"
          aria-label="To date"
          className={selectCls}
        />
        <select value={sort} onChange={e => setSort(e.target.value as typeof SORTS[number]['v'])} className={selectCls}>
          {SORTS.map(s => <option key={s.v} value={s.v}>{s.label}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
        <Stat label="Attendees" value={`${totals.attendees}`} />
        <Stat label="Checked in" value={`${totals.checkedIn}`} />
        <Stat label="Revenue" value={formatCurrency(totals.revenue)} />
        <Stat label="Cancelled" value={`${totals.cancelled}`} />
        <Stat label="Refunded" value={formatCurrency(totals.refunded)} />
      </div>

      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl overflow-hidden">
        {isLoading ? (
          <p className="text-gray-400 text-sm p-6">Loading…</p>
        ) : bookings.length === 0 ? (
          <p className="text-gray-500 text-sm p-6 text-center">No bookings match the current filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.02] text-[10px] uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="text-left p-3">Customer</th>
                  <th className="text-left p-3">Event</th>
                  <th className="text-left p-3">Visit</th>
                  <th className="text-right p-3">Qty</th>
                  <th className="text-right p-3">Paid</th>
                  <th className="text-left p-3">Payment</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-right p-3">Code</th>
                  {(canCancel || canDelete) && <th className="text-right p-3">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {bookings.map(b => (
                  <tr key={b.id} className="border-t border-[#2e2e2e] hover:bg-white/[0.02]">
                    <td className="p-3">
                      <div className="text-white font-medium truncate max-w-[180px]">{b.user_name}</div>
                      <div className="text-[11px] text-gray-500">{b.user_phone}</div>
                    </td>
                    <td className="p-3">
                      <div className="text-gray-200 truncate max-w-[200px]">{b.event_name_en}</div>
                      {b.tier_name && <div className="text-[11px] text-gray-500">{b.tier_name}</div>}
                    </td>
                    <td className="p-3">
                      <div className="text-gray-300 text-xs">
                        {b.attendance_date ?? b.event_starts_at.slice(0, 10)}
                        {b.attendance_time ? ` · ${b.attendance_time}` : ''}
                      </div>
                    </td>
                    <td className="p-3 text-right text-gray-300">{b.quantity}</td>
                    <td className="p-3 text-right text-gray-300">{formatCurrency(b.total_paid_fils)}</td>
                    <td className="p-3">
                      <div className="text-xs text-gray-300">{b.payment_method}</div>
                      {b.card_brand && b.card_last4 && (
                        <div className="text-[11px] text-gray-500">{b.card_brand} •••• {b.card_last4}</div>
                      )}
                    </td>
                    <td className="p-3">
                      <StatusBadge status={b.status} />
                      {b.status === 'cancelled' && b.payment_method === 'card' && (
                        <div className={`text-[10px] mt-0.5 ${b.refund_transaction_id ? 'text-emerald-400' : 'text-amber-400'}`}>
                          {b.refund_transaction_id ? 'Refunded' : 'No refund'}
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-right">
                      {b.status === 'cancelled' ? (
                        <span className="font-mono text-xs text-gray-600">—</span>
                      ) : (
                        <span className="font-mono text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 px-2 py-1 rounded">
                          {b.redemption_code}
                        </span>
                      )}
                    </td>
                    {(canCancel || canDelete) && (
                      <td className="p-3 text-right whitespace-nowrap">
                        {canCancel && b.status === 'confirmed' && (
                          <button
                            type="button"
                            onClick={() => setCancelTarget(b)}
                            title="Cancel booking"
                            className="text-amber-400 hover:text-amber-300 p-1 inline-flex"
                          >
                            <XCircle size={16} />
                          </button>
                        )}
                        {canDelete && (
                          <button
                            type="button"
                            onClick={() => setDeleteTarget(b)}
                            title="Delete booking (permanent)"
                            className="text-red-500 hover:text-red-400 p-1 inline-flex ml-1"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {cancelTarget && (
        <CancelDialog
          booking={cancelTarget}
          onClose={() => setCancelTarget(null)}
          onDone={() => { setCancelTarget(null); refresh() }}
        />
      )}
      {deleteTarget && (
        <DeleteDialog
          booking={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDone={() => { setDeleteTarget(null); refresh() }}
        />
      )}
    </div>
  )
}

function CancelDialog({ booking, onClose, onDone }: { booking: Booking; onClose: () => void; onDone: () => void }) {
  const [reasonCode, setReasonCode] = useState('')
  const [reasonText, setReasonText] = useState('')
  const isCard = booking.payment_method === 'card'
  const [refund, setRefund] = useState(isCard)
  const [error, setError] = useState<string | null>(null)

  const otherNeedsText = reasonCode === 'other' && !reasonText.trim()
  const canSubmit = reasonCode !== '' && !otherNeedsText

  const cancel = useMutation({
    mutationFn: () =>
      api.post(`/events/tickets/${booking.id}/cancel`, {
        refund,
        reason_code: reasonCode,
        reason: reasonText.trim() || undefined,
      }).then(r => r.data),
    onSuccess: onDone,
    onError: (e: unknown) => {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message
      setError(msg ?? 'Could not cancel the booking.')
    },
  })

  return (
    <Modal title="Cancel booking" onClose={onClose}>
      <p className="text-xs text-gray-400 mb-3">
        {booking.user_name} · {booking.event_name_en} · {booking.quantity} ticket{booking.quantity === 1 ? '' : 's'} · {formatCurrency(booking.total_paid_fils)}
      </p>

      <label className="block text-[11px] uppercase tracking-wide text-gray-500 mb-1">Reason</label>
      <select value={reasonCode} onChange={e => setReasonCode(e.target.value)} className={selectCls}>
        <option value="">Select a reason…</option>
        {CANCEL_REASONS.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
      </select>

      {reasonCode === 'other' && (
        <input
          type="text"
          value={reasonText}
          onChange={e => setReasonText(e.target.value)}
          placeholder="Describe the reason"
          maxLength={500}
          className={`${selectCls} mt-2`}
        />
      )}

      {isCard && booking.status === 'confirmed' && (
        <label className="flex items-center gap-2 mt-3 text-sm text-gray-300 cursor-pointer">
          <input type="checkbox" checked={refund} onChange={e => setRefund(e.target.checked)} />
          Refund the card payment ({formatCurrency(booking.total_paid_fils)})
        </label>
      )}

      {error && <p className="text-red-400 text-xs mt-3">{error}</p>}

      <div className="flex justify-end gap-2 mt-5">
        <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-300 hover:text-white">
          Keep booking
        </button>
        <button
          type="button"
          disabled={!canSubmit || cancel.isPending}
          onClick={() => { setError(null); cancel.mutate() }}
          className="px-4 py-2 rounded-lg text-sm font-semibold bg-amber-600 text-white hover:bg-amber-500 disabled:opacity-40"
        >
          {cancel.isPending ? 'Cancelling…' : (isCard && refund ? 'Cancel + refund' : 'Cancel booking')}
        </button>
      </div>
    </Modal>
  )
}

function DeleteDialog({ booking, onClose, onDone }: { booking: Booking; onClose: () => void; onDone: () => void }) {
  const [error, setError] = useState<string | null>(null)
  const del = useMutation({
    mutationFn: () => api.delete(`/events/tickets/${booking.id}`).then(r => r.data),
    onSuccess: onDone,
    onError: (e: unknown) => {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message
      setError(msg ?? 'Could not delete the booking.')
    },
  })

  return (
    <Modal title="Delete booking permanently?" onClose={onClose}>
      <p className="text-sm text-gray-300">
        {booking.user_name} · {booking.event_name_en} · {booking.quantity} ticket{booking.quantity === 1 ? '' : 's'}
      </p>
      <p className="text-xs text-amber-400 mt-2">
        This removes the booking entirely (for test or erroneous entries). No refund is issued. To reverse a real customer's payment, use Cancel instead.
      </p>
      {error && <p className="text-red-400 text-xs mt-3">{error}</p>}
      <div className="flex justify-end gap-2 mt-5">
        <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-300 hover:text-white">Keep</button>
        <button
          type="button"
          disabled={del.isPending}
          onClick={() => { setError(null); del.mutate() }}
          className="px-4 py-2 rounded-lg text-sm font-semibold bg-red-600 text-white hover:bg-red-500 disabled:opacity-40"
        >
          {del.isPending ? 'Deleting…' : 'Delete permanently'}
        </button>
      </div>
    </Modal>
  )
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl p-5 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <h3 className="text-white font-bold mb-3">{title}</h3>
        {children}
      </div>
    </div>
  )
}

const selectCls = 'w-full px-3 py-2 rounded-lg bg-white/5 border border-[#2e2e2e] text-white text-sm focus:outline-none focus:border-[#C41E3A]/40'

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg p-3">
      <p className="text-[10px] uppercase tracking-wide text-gray-500">{label}</p>
      <p className="text-lg font-bold text-white">{value}</p>
    </div>
  )
}

function StatusBadge({ status }: { status: Booking['status'] }) {
  const cls = status === 'used'
    ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
    : status === 'cancelled'
      ? 'bg-gray-500/15 text-gray-400 border-gray-500/30'
      : 'bg-blue-500/15 text-blue-400 border-blue-500/30'
  return <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase border ${cls}`}>{status}</span>
}
